
export type MediaKind = 'image' | 'video' | 'pdf' | 'file';


export interface MediaTypeInput {
  type?: string | null;
  mimeType?: string | null;
  fileName?: string | null;
  name?: string | null;
  file?: File | null;
}

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif', 'avif'];
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'webm', 'mkv', 'm4v', '3gp', 'qt'];
const PDF_EXTENSIONS = ['pdf'];

/** Collect every type-ish string the input carries, lower-cased and trimmed. */
function collectTypeTokens(input: MediaTypeInput): string[] {
  const tokens = [input.type, input.mimeType, input.file?.type]
    .filter((t): t is string => typeof t === 'string' && t.trim().length > 0)
    .map((t) => t.trim().toLowerCase());
  return tokens;
}

/** Collect every file-name string the input carries. */
function collectNames(input: MediaTypeInput): string[] {
  return [input.fileName, input.name, input.file?.name]
    .filter((n): n is string => typeof n === 'string' && n.trim().length > 0)
    .map((n) => n.trim().toLowerCase());
}

/** Extract a lower-cased extension (without the dot) from a file name, or `''`. */
function getExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1 || lastDot === fileName.length - 1) return '';
  return fileName.slice(lastDot + 1);
}

/** Does any collected name have an extension in `extensions`? */
function namesMatchExtension(names: string[], extensions: string[]): boolean {
  return names.some((name) => extensions.includes(getExtension(name)));
}

export function getMediaKind(input: MediaTypeInput | null | undefined): MediaKind {
  if (!input) return 'file';

  const tokens = collectTypeTokens(input);
  const names = collectNames(input);

  // --- PDF ---
  if (
    tokens.some((t) => t === 'application/pdf' || t === 'pdf') ||
    namesMatchExtension(names, PDF_EXTENSIONS)
  ) {
    return 'pdf';
  }

  // --- Image ---
  if (
    tokens.some((t) => t === 'image' || t.startsWith('image/')) ||
    namesMatchExtension(names, IMAGE_EXTENSIONS)
  ) {
    return 'image';
  }

  // --- Video ---
  if (
    tokens.some((t) => t === 'video' || t.startsWith('video/')) ||
    namesMatchExtension(names, VIDEO_EXTENSIONS)
  ) {
    return 'video';
  }

  // --- Generic file (includes 'document', 'file', application/*, unknown) ---
  return 'file';
}

/** True when the input resolves to an image. */
export function isImageMedia(input: MediaTypeInput | null | undefined): boolean {
  return getMediaKind(input) === 'image';
}

/** True when the input resolves to a video. */
export function isVideoMedia(input: MediaTypeInput | null | undefined): boolean {
  return getMediaKind(input) === 'video';
}

/** True when the input resolves to a PDF. */
export function isPdfMedia(input: MediaTypeInput | null | undefined): boolean {
  return getMediaKind(input) === 'pdf';
}

/**
 * True when the input is something we can render an inline preview for today:
 * image, video, or PDF. Generic files are not previewable.
 */
export function isPreviewableMedia(input: MediaTypeInput | null | undefined): boolean {
  const kind = getMediaKind(input);
  return kind === 'image' || kind === 'video' || kind === 'pdf';
}
